import React, { MutableRefObject, RefObject, useCallback, useContext, useEffect, useLayoutEffect, useRef, useState } from 'react';
import clsx from 'clsx';
import { debounce, isEmpty, isEqual, isUndefined } from 'lodash';
import useStayScrolled from 'react-stay-scrolled';
import useDataSheetDialogActions from 'common/hooks/useDataSheetDialogActions';
import ColumnIndicator from 'components/FeaturedSpreadsheet/components/ColumnIndicator';
import DraggableColumns from 'components/FeaturedSpreadsheet/components/DraggableColumns';
import HiddenColumnsChip from 'components/FeaturedSpreadsheet/components/HiddenColumnsChip';
import Row from 'components/FeaturedSpreadsheet/components/Row';
import FeaturedSpreadsheetContext from 'components/FeaturedSpreadsheet/context/FeaturedSpreadsheetContext';
import { NativeScroll } from 'components/NativeScroll';
import ScalarDatasheet from 'components/ScalarDatasheet';
import getSpreadsheetCellRuntime, {
  getEditableDatasheetValueRenderer,
  withDatasheetUpdateIdentity,
} from 'components/ScalarSpreadsheet/utilities/getSpreadsheetCellRuntime';
import { ValueEditor } from 'components/Spreadsheet/components';
import { useLayoutContext } from 'context/LayoutContext';
import { ICell } from 'models/interfaces/spreadsheet-types/core-types';
import { useScalarSpreadsheetStore } from 'store';
import { getNumberValue, getObjectValue, isCellNavigable } from 'utilities';
import { getColumnLetter } from 'utilities/alphabet-utilities';
import ContentColumnsProps from './ContentColumns.props';
import { classes, Root } from './ContentColumns.styles';
import {
  COLUMN_INDICATOR_HEIGHT,
  FIRST_ROWS,
  NATIVE_SCROLL_BOTTOM_GUTTER,
  prependClassname,
} from './ContentColumns.util';

const ContentColumns = ({
  data,
  className = '',
  tableSlug = '',
  isLedgerTable = false,
  disableVerticalScroll,
  disablePerfectScrollbar,
  virtualization,
  disableLegendSelection,
  enableCopyFeedback,
  frozenPaneColumnCellKey,
  onToggleFrozenColumnsAtCell,
  reserveColumnIndicatorSpace,
  useDisplayCellRuntime = false,
  getCellIdentity,
  isFillCompatible,
  onBulkFill,
  onSelectionCommit,
  onSelectionSync,
  highlightSelectedRows,
  highlightSelectedColumns,
}: ContentColumnsProps) => {
  const { cellRenderer, valueViewer } = getSpreadsheetCellRuntime(useDisplayCellRuntime);
  const [hiddenColumns, setHiddenColumns] = useState({ right: 0, left: 0 });
  const [needsScrollbar, setNeedsScrollbar] = useState(false);
  const [tableHeight, setTableHeight] = useState({});
  const scrollbarId = `scrollbar-${tableSlug}`;
  const { showAlert } = useLayoutContext();
  const [isLedgerOpen, setIsLedgerOpen] = useState(false);
  const { dialogs } = useDataSheetDialogActions();

  const scrollbarRef = useRef<HTMLElement | null>(null);
  const scrollbarContainer = scrollbarRef.current;
  const isMounted = useRef(true);

  const {
    cells,
    onCellsChanged,
    collapsibleColumns = {},
    displayLegend,
    selectedCell,
  } = useContext(FeaturedSpreadsheetContext);

  const contentColumnsRef = useRef<HTMLDivElement | null>(null);
  const tableContainerRef = useRef<HTMLTableCellElement | null>(null);
  const spreadsheetTableRef = tableContainerRef as unknown as RefObject<HTMLElement>;

  // ScalarSpreadsheet Store
  const synchronizedBy = useScalarSpreadsheetStore(state => state.synchronizedBy);
  const synchronizedScrollLeft = useScalarSpreadsheetStore(state => state.synchronizedScrollLeft);
  const synchronizedSpreadsheets = useScalarSpreadsheetStore(state => state.synchronizedSpreadsheets);
  const setSynchronizedScroll = useScalarSpreadsheetStore(state => state.setSynchronizedScroll);

  useLayoutEffect(() => {
    if (virtualization) {
      setTableHeight({});
      return;
    }
    const nativeScrollBottomGutter = !disablePerfectScrollbar && needsScrollbar ? NATIVE_SCROLL_BOTTOM_GUTTER : 0;
    const height = {
      ...(tableContainerRef.current?.offsetHeight
        ? {
          height: `${
              getNumberValue(tableContainerRef.current?.offsetHeight) +
              COLUMN_INDICATOR_HEIGHT +
              nativeScrollBottomGutter
            }px`,
        }
        : {}),
    };
    setTableHeight(height);
  }, [data, disablePerfectScrollbar, displayLegend, needsScrollbar, tableContainerRef, tableSlug, virtualization]);

  useStayScrolled(contentColumnsRef as RefObject<HTMLElement>);

  useEffect(
    () => () => {
      isMounted.current = false;
    },
    []
  );

  // Synchronize Scroll
  const handleSynchronizedScroll = useCallback(() => {
    if (!isMounted.current || !scrollbarContainer) {
      return;
    }

    requestAnimationFrame(() => {
      if (!isMounted.current || !scrollbarContainer) {
        return;
      }

      const { scrollLeft = 0 } = getObjectValue(scrollbarContainer);
      setSynchronizedScroll({ synchronizedBy: tableSlug, synchronizedScrollLeft: scrollLeft });
    });
  }, [scrollbarContainer, setSynchronizedScroll, tableSlug]);

  const updateScrollbarState = useCallback(() => {
    const tableElement = tableContainerRef.current;

    if (!tableElement) {
      return;
    }

    const { offsetWidth, scrollWidth } = tableElement;
    // Add a small threshold (2px) to account for rounding differences with zoom
    // This prevents false positives where scrollWidth is slightly larger than offsetWidth
    // due to sub-pixel rendering or zoom calculations
    const SCROLL_THRESHOLD = 2;
    const needsScroll = scrollWidth > offsetWidth + SCROLL_THRESHOLD;

    setNeedsScrollbar(prev => (prev !== needsScroll ? needsScroll : prev));
  }, [tableContainerRef]);

  const getHiddenColumns = useCallback(
    (container?: HTMLDivElement) => {
      if (!isMounted.current || !scrollbarContainer) {
        return;
      }

      let currentHiddenColumnsRight = 0;
      let currentHiddenColumnsLeft = 0;

      let { scrollLeft = 0 } = getObjectValue(scrollbarContainer);

      if (container) {
        scrollLeft = container.scrollLeft;
      }

      if (tableContainerRef.current) {
        const containerWidth = tableContainerRef.current?.offsetWidth;

        const columns: { [key: number]: NodeListOf<Element> } = {};
        const columnsOffsetLeft: { [key: number]: number } = {};

        FIRST_ROWS.forEach(row => {
          const cols = tableContainerRef.current?.querySelectorAll(`table tbody tr:nth-child(${row}) td`);
          const totalOffsetLeft = Array.from(columns as ArrayLike<NodeListOf<Element>>).reduce(
            (accumulator: number, current: any) => accumulator + current.offsetLeft,
            0
          );

          if (!cols) {
            return;
          }

          columns[row] = cols;
          columnsOffsetLeft[row] = totalOffsetLeft;
        });

        const longestRow = Object.keys(columnsOffsetLeft).reduce((a, b): number => {
          const aOffset = columnsOffsetLeft[a];
          const bOffset = columnsOffsetLeft[+b];

          return aOffset > bOffset ? a : +b;
        }, 0);

        columns?.[longestRow]?.forEach((col: any) => {
          if (col.offsetLeft >= containerWidth + scrollLeft) {
            currentHiddenColumnsRight += 1;
          } else if (col.offsetLeft > containerWidth) {
            currentHiddenColumnsLeft += 1;
          }
        });

        const currentHiddenColumns = Object.freeze({
          right: currentHiddenColumnsRight,
          left: currentHiddenColumnsLeft,
        });

        setHiddenColumns(prev => (isEqual(prev, currentHiddenColumns) ? prev : currentHiddenColumns));
      }
    },
    [scrollbarContainer]
  );

  const handleScrollX = debounce(() => {
    getHiddenColumns();
  }, 100);

  useEffect(() => {
    const handleResize = () => {
      getHiddenColumns();
    };

    window.addEventListener('resize', handleResize);

    return () => {
      window.removeEventListener('resize', handleResize);
    };
  }, [getHiddenColumns]);

  useLayoutEffect(() => {
    getHiddenColumns();
    updateScrollbarState();
  }, [getHiddenColumns, updateScrollbarState]);

  useEffect(() => {
    if (!isEmpty(collapsibleColumns) || (tableContainerRef.current?.offsetWidth ?? 0) > 0) {
      getHiddenColumns();
      updateScrollbarState();
    }
  }, [collapsibleColumns, getHiddenColumns, updateScrollbarState]);

  useEffect(() => {
    const handleZoomChange = () => {
      setTimeout(() => {
        updateScrollbarState();
        getHiddenColumns();
      }, 100);
    };

    const observer = new MutationObserver(mutations => {
      mutations.forEach(mutation => {
        if (mutation.type === 'attributes' && mutation.attributeName === 'style') {
          const target = mutation.target as HTMLElement;
          if (target.style.zoom) {
            handleZoomChange();
          }
        }
      });
    });

    const main = document.querySelector('main');
    if (main) {
      observer.observe(main, { attributes: true, attributeFilter: ['style'] });

      const tabPanels = main.querySelectorAll('[id^="navigations-tabs-tabpanel-"]');
      tabPanels.forEach(panel => {
        observer.observe(panel, { attributes: true, attributeFilter: ['style'] });
      });
    }

    const handleResize = debounce(() => {
      updateScrollbarState();
      getHiddenColumns();
    }, 100);

    window.addEventListener('resize', handleResize);

    let resizeObserver: ResizeObserver | null = null;
    if (tableContainerRef.current) {
      resizeObserver = new ResizeObserver(
        debounce(() => {
          updateScrollbarState();
          getHiddenColumns();
        }, 100)
      );
      resizeObserver.observe(tableContainerRef.current);
    }

    return () => {
      observer.disconnect();
      window.removeEventListener('resize', handleResize);
      if (resizeObserver) {
        resizeObserver.disconnect();
      }
    };
  }, [updateScrollbarState, getHiddenColumns]);

  useEffect(() => {
    updateScrollbarState();
  }, [needsScrollbar, updateScrollbarState]);

  useEffect(() => {
    const debouncedScroll = debounce(handleSynchronizedScroll, 0);
    if (scrollbarContainer && synchronizedSpreadsheets.includes(tableSlug)) {
      scrollbarContainer.addEventListener('scroll', debouncedScroll, { passive: true });
    }

    return () => {
      if (scrollbarContainer) {
        scrollbarContainer.removeEventListener('scroll', debouncedScroll);
      }
    };
  }, [handleSynchronizedScroll, scrollbarContainer, synchronizedSpreadsheets, tableSlug]);

  useEffect(() => {
    if (scrollbarContainer && synchronizedSpreadsheets.includes(tableSlug) && synchronizedBy !== tableSlug) {
      requestAnimationFrame(() => {
        if (isMounted.current && scrollbarContainer) {
          scrollbarContainer.scrollLeft = synchronizedScrollLeft;
        }
      });
    }
  }, [scrollbarContainer, synchronizedBy, synchronizedScrollLeft, synchronizedSpreadsheets, tableSlug]);

  const getInvalidCellElement = (invalidCell: ICell) => {
    if (invalidCell) {
      return document.getElementById(`CELL-${invalidCell?.key}`) ?? undefined;
    }
    return undefined;
  };

  const moveScrollbarToInvalidCell = useCallback(
    (cellElement?: HTMLElement) => {
      if (cellElement && scrollbarContainer) {
        scrollbarContainer.scrollLeft = cellElement.offsetLeft;
      }
    },
    [scrollbarContainer]
  );

  useEffect(() => {
    if (showAlert && cells && scrollbarContainer) {
      const invalidCell = Object.values(cells).find(cell => !isEmpty(cell.tooltipMessages));
      const invalidCellElement = getInvalidCellElement(invalidCell as ICell);
      moveScrollbarToInvalidCell(invalidCellElement);
    }
  }, [cells, moveScrollbarToInvalidCell, scrollbarContainer, showAlert]);

  const handleChipClickRight = useCallback(() => {
    if (scrollbarContainer) {
      scrollbarContainer.scrollLeft += scrollbarContainer.clientWidth;
    }
  }, [scrollbarContainer]);

  const handleChipClickLeft = useCallback(() => {
    if (scrollbarContainer) {
      scrollbarContainer.scrollLeft -= scrollbarContainer.clientWidth;
    }
  }, [scrollbarContainer]);

  useEffect(() => {
    if (!isLedgerTable && dialogs.find((dialog: any) => !isUndefined(dialog.cellId) && dialog.disableWhenLedgerOpen)) {
      setIsLedgerOpen(true);
    }

    return () => {
      setIsLedgerOpen(false);
    };
  }, [dialogs, isLedgerTable]);

  const dataRenderer = useCallback((cell: ICell) => (cell?.expr ? cell.expr : cell?.value), []);

  const hasCollapsibleColumns = !isEmpty(collapsibleColumns);
  const attributesRenderer = useCallback(
    (cell: ICell) => {
      const columnLegend = cell.columnLegend ?? cell.colLegend;
      const selectedColumnLegend =
        selectedCell?.cellInfo?.columnLegend ??
        (selectedCell?.start ? getColumnLetter(selectedCell.start.j) : undefined);
      const isSelectedYearReference =
        columnLegend &&
        columnLegend === selectedColumnLegend &&
        typeof cell.className === 'string' &&
        cell.className.includes('xlsx-internal-reference-cell');

      return {
        ...(cell.key ? { id: `CELL-${cell.key}` } : {}),
        'data-parent': cell.parent ?? '',
        'data-is-parent': (cell.isParent && hasCollapsibleColumns) ?? '',
        'data-col-parent': cell.parentColumn ?? '',
        'data-child-index': cell.childIndex ?? '',
        'data-column-legend': columnLegend,
        'data-column-id': cell.columnId ?? 0,
        'data-column-ref': cell.columnRef ?? 0,
        ...(cell.x !== undefined ? { 'data-row-index': String(cell.x) } : {}),
        ...(isSelectedYearReference ? { 'data-xlsx-selected-year-reference': 'true' } : {}),
        // SCAL-8976: lets the review panel address a cell by (securityId, fieldAlias)
        // via `td[data-column-id="..."][data-alias="..."]` for the hover/active outline.
        ...(cell.alias ? { 'data-alias': cell.alias } : {}),
        ...(cell.testId ? { 'data-testid': cell.testId } : {}),
      };
    },
    [hasCollapsibleColumns, selectedCell]
  );
  const contentRootStyle = {
    ...tableHeight,
    ...(reserveColumnIndicatorSpace ? { boxSizing: 'border-box' as const, paddingTop: COLUMN_INDICATOR_HEIGHT } : {}),
  };

  const content = (
    <>
      <ColumnIndicator
        frozenPaneCellKey={frozenPaneColumnCellKey}
        hiddenColumns={hiddenColumns}
        onToggleFrozenPaneAtCell={onToggleFrozenColumnsAtCell}
        tableRef={spreadsheetTableRef}
      />
      <DraggableColumns tableRef={spreadsheetTableRef}>
        <Root ref={tableContainerRef}>
          <ScalarDatasheet
            className={prependClassname(className, 'spreadsheet')}
            dataRenderer={dataRenderer}
            onCellsChanged={onCellsChanged}
            data={data}
            virtualization={virtualization}
            rowRenderer={Row}
            dataEditor={ValueEditor}
            valueViewer={valueViewer}
            cellRenderer={cellRenderer}
            attributesRenderer={withDatasheetUpdateIdentity(attributesRenderer, useDisplayCellRuntime)}
            valueRenderer={getEditableDatasheetValueRenderer(useDisplayCellRuntime)}
            allowColumnLegendSelection={Boolean(onToggleFrozenColumnsAtCell)}
            disableLegendSelection={disableLegendSelection}
            enableCopyFeedback={enableCopyFeedback}
            getCellIdentity={getCellIdentity}
            isFillCompatible={isFillCompatible}
            onBulkFill={onBulkFill}
            onSelectionCommit={onSelectionCommit}
            onSelectionSync={onSelectionSync}
            highlightSelectedRows={highlightSelectedRows}
            highlightSelectedColumns={highlightSelectedColumns}
            isCellNavigable={cell =>
              isCellNavigable({ cell, containerRef: tableContainerRef as MutableRefObject<HTMLTableCellElement> })
            }
            {...(isLedgerOpen && { selected: null })}
          />
        </Root>
      </DraggableColumns>
    </>
  );

  const renderScrollContainer = () => {
    // The datasheet owns both axes when virtualized; an outer NativeScroll
    // would steal wheel events from the titles column's matching scroller.
    // Keep the 12px column-indicator gutter so split title/content panes stay aligned.
    if (virtualization) {
      return (
        <div
          style={
            disablePerfectScrollbar ? undefined : { boxSizing: 'border-box', paddingTop: COLUMN_INDICATOR_HEIGHT }
          }>
          {content}
        </div>
      );
    }

    if (disablePerfectScrollbar) {
      return <div>{content}</div>;
    }

    if (!needsScrollbar) {
      return <div style={{ overflow: 'visible', paddingTop: reserveColumnIndicatorSpace ? 0 : '12px' }}>{content}</div>;
    }

    return (
      <NativeScroll
        className={prependClassname(className, 'scrollbar')}
        containerRef={el => {
          scrollbarRef.current = el;
        }}
        id={scrollbarId}
        onScrollX={handleScrollX}
        options={{ suppressScrollY: disableVerticalScroll ? true : !needsScrollbar, suppressScrollX: !needsScrollbar }}>
        {content}
      </NativeScroll>
    );
  };

  // eslint-disable-next-line react/no-unknown-property
  const inertProps = isLedgerOpen ? ({ inert: '' } as Record<string, string>) : {};

  return (
    <div ref={contentColumnsRef} className={classes.columnsContent} style={contentRootStyle} {...inertProps}>
      <div className={clsx(hiddenColumns.left && classes.borderedLeft)} />
      <div className={clsx(hiddenColumns.right && classes.borderedRight)} />
      <HiddenColumnsChip
        isVisible={!!hiddenColumns.right}
        label={`${hiddenColumns.right} column${hiddenColumns.right > 1 ? 's' : ''}`}
        handleClick={handleChipClickRight}
        atRight
      />
      <HiddenColumnsChip
        isVisible={!!hiddenColumns.left}
        label={`${hiddenColumns.left} column${hiddenColumns.left > 1 ? 's' : ''}`}
        handleClick={handleChipClickLeft}
        atRight={false}
      />
      {renderScrollContainer()}
    </div>
  );
};

export default React.memo(
  ContentColumns,
  (prevProps, nextProps) =>
    prevProps.data === nextProps.data &&
    prevProps.disableLegendSelection === nextProps.disableLegendSelection &&
    prevProps.enableCopyFeedback === nextProps.enableCopyFeedback &&
    prevProps.frozenPaneColumnCellKey === nextProps.frozenPaneColumnCellKey &&
    prevProps.onToggleFrozenColumnsAtCell === nextProps.onToggleFrozenColumnsAtCell &&
    prevProps.reserveColumnIndicatorSpace === nextProps.reserveColumnIndicatorSpace &&
    prevProps.virtualization === nextProps.virtualization &&
    prevProps.useDisplayCellRuntime === nextProps.useDisplayCellRuntime
);
